import { useState } from "react";

import { getMentionText, MENTION_NODE_NAME } from "@corca/tiptap";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { NodeViewWrapper } from "@tiptap/react";
import { observer } from "mobx-react-lite";

import { useStore } from "@/store";
import { teamPageUrl } from "@/urls";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";
import { useIsMyDoc } from "@/utils/useIsMyDoc";

import { MentionCard } from "./MentionCard";
import { useMentionAccess } from "./useMentionAccess";

import type { MentionOptions } from "../Mention";
import type { TeamModel } from "@/models/TeamModel";
import type { MentionNodeAttrs } from "@corca/tiptap";
import type { NodeViewProps } from "@tiptap/react";

const HOVER_DELAY = { open: 300, close: 120 };

export const MENTION_CLASS = "text-blue-500 hover:underline";

export const MENTION_NO_ACCESS_CLASS = "text-gray-500";

export const MentionView = observer(function MentionView({
  node,
  HTMLAttributes,
  extension,
}: NodeViewProps) {
  const { username, userId } = node.attrs as MentionNodeAttrs;
  const { doc: editorDoc, message, chat: inputChat } = extension.options as MentionOptions;
  const { doc, dialog } = useStore();
  const isMyDoc = useIsMyDoc(doc);
  const [isCardOpen, setIsCardOpen] = useState(false);

  const chat = message?.chat ?? inputChat;
  const user =
    message?.mentions.find((u) => u.uid === userId) ??
    editorDoc.api.mentions.find((u) => u.uid === userId);
  const access = useMentionAccess(userId, chat);

  const inviteToDocument = async () => {
    if (!doc) return;

    try {
      await doc.permissions.createPermission({ permalink: username }, doc.permissions.scopeToGive);
      if (doc.id) trackEvent("share_user_invited", { username, doc_id: doc.id });
    } catch (error) {
      console.error(error);
    }

    dialog.shareDialog.open();
  };

  const inviteToTeam = async (team: TeamModel) => {
    const { invitesUX } = team;
    const pending = invitesUX.invites.some(({ user: invitee }) => invitee?.username === username);

    if (!pending) {
      try {
        await invitesUX.createInvite({ username });
      } catch (error) {
        console.error(error);
      }
    }

    dialog.teamDialog.open(team, "members");
  };

  const invite = () => {
    setIsCardOpen(false);

    const invited = access.team ? inviteToTeam(access.team) : inviteToDocument();
    invited.catch((e) => console.error(e));
  };

  const canInvite = access.scope === "team" ? !!access.team : isMyDoc && !!doc;

  const { refs, floatingStyles, context } = useFloating({
    open: isCardOpen,
    onOpenChange: setIsCardOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  const hover = useHover(context, {
    delay: HOVER_DELAY,
    mouseOnly: true,
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

  const chipProps = {
    ...HTMLAttributes,
    "data-type": MENTION_NODE_NAME,
    className: twMerge(MENTION_CLASS, access.lacksAccess && MENTION_NO_ACCESS_CLASS),
    ref: refs.setReference,
    ...getReferenceProps(),
  };
  const text = getMentionText(node);

  return (
    <NodeViewWrapper as="span">
      {username ? (
        <a
          {...chipProps}
          href={teamPageUrl({ teamPermalink: username })}
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          {text}
        </a>
      ) : (
        <span {...chipProps}>{text}</span>
      )}
      {isCardOpen && username && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="z-50"
            {...getFloatingProps()}
          >
            <MentionCard
              username={username}
              user={user}
              missingAccess={access.lacksAccess ? access.scope : undefined}
              onInvite={canInvite ? invite : undefined}
            />
          </div>
        </FloatingPortal>
      )}
    </NodeViewWrapper>
  );
});
